import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";

import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/ui/text";
import { Icon } from "@/components/ui/icons";
import { PageNavigationHeader } from "@/components/ui/page-navigation-header";
import { Skeleton } from "@/components/ui/skeleton";
import { EntityStatusBadge } from "@/components/shared/entity-status-badge";
import { VStack } from "@/components/ui/v-stack";
import { getAdminLoad } from "@/lib/api/admin/loads";
import { getLoadStatusLabel } from "@/lib/api/loads";
import { fromIntlLanguage } from "@/lib/i18n";
import { formatDate } from "@/lib/formatting";
import { getLocalizedName } from "@/lib/localization";
import {
  LoadDetailsSkeleton,
  LoadDetailsView,
  computeLoadDetailsPolicy,
  type LoadDetailsData,
} from "@/components/load-detail";
import { DeclineLoadDialog } from "@/components/load-detail/decline-load-dialog";
import { ApproveLoadDialog } from "@/components/load-detail/approve-load-dialog";
import { TicketActivitySection } from "@/components/shared/ticket-activity-section";
import { useAdminLoadDetail } from "@/hooks/use-admin-load-detail";

function AdminLoadApprovalDetailsPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tAdminLoadApprovals } = useTranslation("admin.loadApprovals");
  const { t: tAdminLoadApprovalDetails } = useTranslation(
    "admin.loadApprovalDetails",
  );
  const { t: tCommon } = useTranslation("common");
  const { t: tLoads } = useTranslation("loads");
  const params = Route.useParams();
  const id = params?.id;

  const {
    data: load = null,
    isLoading: loading,
    isError: errorKind,
    refetch,
  } = useQuery({
    queryKey: queryKeys.admin.load(id ?? ""),
    queryFn: () => getAdminLoad(id!),
    enabled: !!id,
  });
  const queryClient = useQueryClient();
  const { state, actions } = useAdminLoadDetail();
  const { submitting, didMutate, showDeclineDialog, showApproveDialog } = state;

  const canApprove = load?.status === "PendingApproval";
  const statusLabel = load ? getLoadStatusLabel(load.status, tLoads) : null;

  const details = load as unknown as LoadDetailsData | null;
  const policy = details
    ? computeLoadDetailsPolicy({
        viewerRole: "admin",
        status: details.status,
        hasTrip: Boolean(details.trip),
      })
    : null;

  return (
    <VStack gap="xl" data-testid="admin-load-approval-details-page">
      <PageNavigationHeader
        contextLabel={tAdminLoadApprovals("title")}
        title={
          loading ? (
            <Skeleton className="h-7 w-48" />
          ) : load ? (
            `${load.load_id}: ${getLocalizedName(load.load_type, language)}`
          ) : (
            tAdminLoadApprovalDetails("titleFallback")
          )
        }
        metadata={
          load
            ? `${tLoads("created")} ${formatDate(load.created_at, language)}`
            : tCommon("loading")
        }
        status={
          load && statusLabel ? (
            <EntityStatusBadge entityType="load" status={load.status}>
              {statusLabel}
            </EntityStatusBadge>
          ) : (
            <Skeleton className="h-7 w-24" rounded="full" />
          )
        }
        actions={
          <>
            {load &&
              load.status !== "PendingApproval" &&
              load.status !== "Declined" && (
                <Button
                  color="neutral"
                  variant="outline"
                  onClick={() =>
                    void navigate({
                      to: "/admin/loads/$id/trades",
                      params: { id },
                    })
                  }
                >
                  <Icon.Negotiations size="md" className="mr-2" />
                  {tAdminLoadApprovalDetails("viewTrades")}
                </Button>
              )}
            <Button
              color="danger"
              disabled={!canApprove || submitting}
              onClick={actions.openDeclineDialog}
              data-testid="load-decline-btn"
            >
              {tAdminLoadApprovalDetails("declineLoad")}
            </Button>
            <Button
              color="success"
              disabled={!canApprove || submitting}
              loading={submitting}
              onClick={actions.openApproveDialog}
              data-testid="load-approve-btn"
            >
              {tAdminLoadApprovalDetails("approveLoad")}
            </Button>
          </>
        }
        onBack={() => {
          if (didMutate) {
            void navigate({
              to: "/admin/loads",
              search: { refresh: String(Date.now()) },
            });
            return;
          }

          if (typeof window !== "undefined" && window.history.length > 1) {
            router.history.back();
            return;
          }

          void navigate({ to: "/admin/loads" });
        }}
        backAriaLabel={tCommon("back")}
        contextAriaLabel={tAdminLoadApprovalDetails("backToLoadApprovals")}
      />

      {load?.tickets ? <TicketActivitySection tickets={load.tickets} /> : null}

      {errorKind ? (
        <ErrorState
          title={tAdminLoadApprovalDetails("failedToLoadLoad")}
          action={
            <Button
              color="neutral"
              variant="outline"
              onClick={() => void refetch()}
            >
              {tCommon("retry")}
            </Button>
          }
          className="py-10"
        />
      ) : null}

      {loading ? <LoadDetailsSkeleton /> : null}

      {!loading && details && policy ? (
        <LoadDetailsView data={details} policy={policy} />
      ) : null}

      {id ? (
        <DeclineLoadDialog
          loadId={id}
          open={showDeclineDialog}
          onClose={actions.closeDeclineDialog}
          onSuccess={async () => {
            await queryClient.invalidateQueries({
              queryKey: queryKeys.admin.load(id),
            });
            actions.markMutated();
          }}
        />
      ) : null}

      {id ? (
        <ApproveLoadDialog
          loadId={id}
          open={showApproveDialog}
          onClose={actions.closeApproveDialog}
          onSuccess={async () => {
            await queryClient.invalidateQueries({
              queryKey: queryKeys.admin.load(id),
            });
            actions.markMutated();
          }}
        />
      ) : null}
    </VStack>
  );
}

export const Route = createFileRoute("/_app/admin/loads/$id")({
  component: AdminLoadApprovalDetailsPage,
});
